/*!
 * @copyright
 * Copyright(c) 2022 SICK AG
 */
export const IS_TOUCH = window.navigator.maxTouchPoints > 0 || !!('ontouchstart' in window);
/**
 * Repeadedly execute a callback, when long-pressing an element
 * @param element element to register the callback for
 * @param onSpin (spinning: boolean) => void Is called
 *    1) every <intervalMs> milliseconds, when <element> is permanently pressed (parameter spinning = true),
 *    2) once, when <element> is only clicked (parameter spinning = false).
 * @param onSpinEnd callback is called, when setInterval was started (we are "spinning") and long-press ends
 * @param intervalMs rate for calling <onSpin>
 * @param timeoutMs delay befor starting to repeatedly call <onSpin>.
 *
 * @returns callback to unregister the dynamically created eventlistener for 'pointerdown'
 */

export function repeatOnLongPress(element, onSpin, onSpinEnd = () => null, intervalMs = 50, timeoutMs = 200) {
  let observer;
  const parentNodes = [];

  const spinOnLongPressCallback = (event, spinCallback) => {
    var _observer2;

    const originalTarget = event.target;
    let interval;
    let timeout;
    event.preventDefault();
    event.stopPropagation();
    timeout = setTimeout(() => {
      timeout = undefined;
      interval = setInterval(() => {
        spinCallback(originalTarget);
      }, intervalMs);
    }, timeoutMs);

    const pointerUpCallback = pointerUp => {
      var _observer;

      pointerUp === null || pointerUp === void 0 ? void 0 : pointerUp.preventDefault();
      pointerUp === null || pointerUp === void 0 ? void 0 : pointerUp.stopPropagation();

      if (timeout) {
        // we did not start to spin
        clearTimeout(timeout);
        onSpin(false); // click
      } else {
        onSpinEnd();
      } // cleanup ...


      interval && clearTimeout(interval);
      document.removeEventListener('pointerup', pointerUpCallback);
      (_observer = observer) === null || _observer === void 0 ? void 0 : _observer.disconnect();

      if (IS_TOUCH) {
        document.removeEventListener('scroll', pointerUpCallback);
        parentNodes.forEach(el => {
          el.removeEventListener('scroll', pointerUpCallback);
        });
      }
    };

    document.addEventListener('pointerup', pointerUpCallback); // for touch-devices to stop on scroll

    if (IS_TOUCH) {
      document.addEventListener('scroll', pointerUpCallback);
      let watchEl = element;

      while (watchEl) {
        var _watchEl$parentNode;

        parentNodes.push(watchEl);
        watchEl.addEventListener('scroll', () => {
          pointerUpCallback();
        });
        watchEl = watchEl.parentElement || ((_watchEl$parentNode = watchEl.parentNode) === null || _watchEl$parentNode === void 0 ? void 0 : _watchEl$parentNode.host);
      }
    } // for native buttons to work, when becomeing disabled during long-press


    (_observer2 = observer) === null || _observer2 === void 0 ? void 0 : _observer2.disconnect();
    observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'disabled') {
          pointerUpCallback();
        }
      });
    });
    observer.observe(element, {
      attributes: true
    });
  };

  const pointerdownCallback = event => {
    if (event.button !== 0) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();
    spinOnLongPressCallback(event, () => {
      onSpin(true);
    });
  };

  element.addEventListener('pointerdown', pointerdownCallback);
  return () => {
    element.removeEventListener('pointerdown', pointerdownCallback);
  };
}
